"use client"

import { useState, useEffect } from "react"

interface SpotCardImageProps {
  spotId: string
  spotName: string
  fallbackUrl?: string
}

interface ImageData {
  unsplash_url: string
  photographer_name: string
  photographer_username: string
  photographer_url: string
}

const DEFAULT_PLACEHOLDER_ID = "0XBjklLq5rg"
const UTM_PARAMS = "utm_source=surf_spots&utm_medium=referral"

export function SpotCardImage({ spotId, spotName, fallbackUrl }: SpotCardImageProps) {
  const [imageData, setImageData] = useState<ImageData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadTopImage()
  }, [spotId])

  const loadTopImage = async () => {
    console.log("SpotCardImage loading for spot:", spotId)
    try {
      // Use API route instead of direct Supabase call
      const response = await fetch(`/api/spot-images?spotId=${spotId}`)
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`)
      }
      
      const data = await response.json()
      console.log("SpotCardImage API response:", data)
      
      // Check if we have a valid image URL (must be a full URL starting with http:// or https://)
      let foundValidImage = false
      if (data.success && data.images && data.images.length > 0 && data.images[0]?.unsplash_url) {
        const imageUrl = data.images[0].unsplash_url
        // Validate that it's a full URL, not a local path
        if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
          console.log("Using community image:", imageUrl)
          setImageData(data.images[0] as ImageData)
          foundValidImage = true
        } else {
          console.log("Invalid image URL (local path), skipping:", imageUrl)
          // Will fall through to fetch default placeholder
        }
      }
      
      // If no valid images found, fetch default placeholder
      if (!foundValidImage) {
        // No images found - fetch default Unsplash placeholder
        console.log("No valid images found, fetching default Unsplash placeholder")
        try {
          const response = await fetch(`/api/unsplash/photo?id=${DEFAULT_PLACEHOLDER_ID}`)
          if (response.ok) {
            const photoData = await response.json()
            if (photoData.fallback || !photoData.user) {
              console.log("Using fallback placeholder due to rate limiting or missing data")
              setImageData(null) // Will fall back to /surf-wave.jpg
            } else {
              console.log("Default placeholder loaded:", photoData.urls.regular)
              setImageData({
                unsplash_url: photoData.urls.regular,
                photographer_name: photoData.user.name,
                photographer_username: photoData.user.username,
                photographer_url: `${photoData.user.profile_url}?${UTM_PARAMS}`,
              })
            }
          } else {
            console.log("Failed to fetch placeholder, using fallback")
            setImageData(null) // Will fall back to /surf-wave.jpg
          }
        } catch (err) {
          console.error("Error fetching placeholder:", err)
          setImageData(null) // Will fall back to /surf-wave.jpg
        }
      }
    } catch (err) {
      console.error("Error loading spot image:", err)
      setImageData(null)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div className="w-full h-40 sm:h-48 bg-muted animate-pulse" />
  }

  return (
    <div className="relative w-full h-40 sm:h-48 overflow-hidden">
      <img
        src={imageData?.unsplash_url || fallbackUrl || "/surf-wave.jpg"}
        alt={spotName}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        onError={(e) => {
          console.error("Image failed to load:", imageData?.unsplash_url || fallbackUrl)
          // Final fallback to standard image
          if (e.currentTarget.src !== "/surf-wave.jpg") {
            e.currentTarget.src = "/surf-wave.jpg"
          }
        }}
      />
    </div>
  )
}
